import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  InputAdornment,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../api';

interface WriteOff {
  id: number;
  deviceId: number;
  device: { id: number; inventoryNumber: string; model: string; type: string };
  date: string;
  reason: string;
}

interface AvailableDevice {
  id: number;
  inventoryNumber: string;
  model: string;
  type: string;
}

export default function WriteOffsPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = useMemo(() => language === 'ru' ? ru : enUS, [language]);
  const [writeOffs, setWriteOffs] = useState<WriteOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const fetchWriteOffs = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/devices?status=written_off&limit=100';
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      const enriched = data.data.map((d: any) => ({
        id: d.writeOffs?.[0]?.id || 0,
        deviceId: d.id,
        device: { id: d.id, inventoryNumber: d.inventoryNumber, model: d.model, type: d.type },
        date: d.writeOffs?.[0]?.date || d.purchaseDate,
        reason: d.writeOffs?.[0]?.reason || '—',
      }));
      setWriteOffs(enriched);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('writeOffs.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, enqueueSnackbar, t]);

  useEffect(() => {
    fetchWriteOffs();
  }, [fetchWriteOffs]);

  const openDialog = async () => {
    try {
      const { data } = await api.get('/devices?status=in_stock&limit=200');
      setAvailableDevices(data.data);
      setDialogOpen(true);
    } catch (err: any) {
      enqueueSnackbar(t('writeOffs.snackbarLoadDevices'), { variant: 'error' });
    }
  };

  const handleWriteOff = async () => {
    if (!selectedDeviceId || !reason.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/devices/${selectedDeviceId}/write-off`, {
        reason,
        date: new Date().toISOString(),
      });
      enqueueSnackbar(t('writeOffs.snackbarWrittenOff'), { variant: 'success' });
      setDialogOpen(false);
      setSelectedDeviceId('');
      setReason('');
      fetchWriteOffs();
    } catch (err: any) {
      const backendError = err.response?.data?.error;
      let errorMessage: string;
      if (backendError === 'Device is already written off') {
        errorMessage = t('writeOffs.writeOffErrorAlreadyWrittenOff');
      } else if (backendError === 'Cannot write off an issued device. Return it first') {
        errorMessage = t('writeOffs.writeOffErrorIssued');
      } else if (backendError === 'Device not found') {
        errorMessage = t('writeOffs.writeOffErrorNotFound');
      } else {
        errorMessage = backendError || t('common.error');
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #f87171 20%, #ef4444 80%)'
                : 'linear-gradient(135deg, #ef4444 20%, #dc2626 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(248,113,113,0.6)'
                : '0 8px 20px -8px rgba(239,68,68,0.4)',
            }}
          >
            <DeleteSweepIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('writeOffs.intro')} · {t('writeOffs.total')}: <strong style={{ color: 'var(--text)' }}>{writeOffs.length}</strong>
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
          {t('writeOffs.newWriteOff')}
        </Button>
      </Box>

      {/* Filter bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: '1 1 300px' }}
            placeholder={t('writeOffs.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
            }}
          />
          {search && (
            <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
              {t('devices.clearFilters')}
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('writeOffs.tableInvNumber')}</TableCell>
                <TableCell>{t('writeOffs.tableModel')}</TableCell>
                <TableCell>{t('writeOffs.tableType')}</TableCell>
                <TableCell>{t('writeOffs.tableDate')}</TableCell>
                <TableCell>{t('writeOffs.tableReason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell>
                </TableRow>
              ) : writeOffs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>
                    {t('writeOffs.noWriteOffs')}
                  </TableCell>
                </TableRow>
              ) : (
                writeOffs.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((w) => (
                  <TableRow key={w.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}
                      >
                        {w.device.inventoryNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{w.device.model}</TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{w.device.type}</TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                      {format(parseISO(w.date), 'dd.MM.yyyy', { locale })}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>{w.reason}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={writeOffs.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.fromToOf', { from, to, count })}
        />
      </Paper>

      {/* New write-off dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('writeOffs.newWriteOff')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>{t('writeOffs.formDevice')}</InputLabel>
            <Select
              value={selectedDeviceId}
              label={t('writeOffs.formDevice')}
              onChange={(e) => setSelectedDeviceId(e.target.value as number)}
            >
              {availableDevices.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.inventoryNumber} — {d.model} ({d.type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={t('writeOffs.formReason')}
            required
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleWriteOff}
            disabled={submitting || !selectedDeviceId || !reason.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : t('writeOffs.writeOffButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}